import React from 'react';
import { CheckCircle, Clock, Navigation, Truck, MapPin, Calendar, User, MapPinIcon } from 'lucide-react';
import { FaseOS } from '../types';
import FaseMap from './FaseMap';

interface TimelineProps {
  fases: FaseOS[];
}

const Timeline: React.FC<TimelineProps> = ({ fases }) => {
  const getStatusIcon = (status: string | null | undefined, nomeFase: string | null | undefined) => {
    if (status === 'Concluída') {
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
    
    if (status === 'Em Rota') {
      return <Navigation className="h-5 w-5 text-blue-500 animate-pulse" />;
    }
    
    // Ícones específicos para cada fase
    switch ((nomeFase || '').toLowerCase()) {
      case 'remoção':
        return <Truck className="h-5 w-5 text-gray-400" />;
      case 'clínica':
        return <MapPin className="h-5 w-5 text-gray-400" />;
      case 'velório':
        return <Clock className="h-5 w-5 text-gray-400" />;
      default:
        return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    switch (status) {
      case 'Concluída':
        return 'border-green-200 bg-green-50';
      case 'Em Rota':
        return 'border-blue-200 bg-blue-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };

  const getStatusText = (status: string | null | undefined) => {
    switch (status) {
      case 'Concluída':
        return 'Concluída';
      case 'Em Rota':
        return 'Em Andamento';
      default:
        return 'Pendente';
    }
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return 'Não informado';
    
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Data inválida';
    }
  };

  const getFaseDescription = (nomeFase: string | null | undefined) => {
    if (!nomeFase) return 'Fase do serviço funerário';
    
    switch (nomeFase.toLowerCase()) {
      case 'remoção':
        return 'Retirada do corpo do local de falecimento';
      case 'clínica':
        return 'Transporte para clínica ou hospital';
      case 'velório':
        return 'Transporte para local do velório';
      case 'sepultamento':
        return 'Transporte para cemitério';
      default:
        return 'Fase do serviço funerário';
    }
  };

  const formatarVeiculo = (veiculo?: { placa: string; prefixo?: string | null; marca?: string | null; modelo?: string | null } | null): string => {
    if (!veiculo) return '';
    
    const marcaModelo = veiculo.marca && veiculo.modelo 
      ? `${veiculo.marca} ${veiculo.modelo}`
      : veiculo.marca 
        ? veiculo.marca
        : veiculo.modelo 
          ? veiculo.modelo
          : '';
    
    if (marcaModelo) {
      return `${marcaModelo} (${veiculo.placa})`;
    }
    
    return veiculo.placa;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6 text-center lg:text-left">
        <h3 className="text-xl lg:text-2xl font-bold text-gray-900 flex items-center justify-center lg:justify-start">
          <Clock className="h-6 w-6 mr-3 text-blue-600" />
          Linha do Tempo do Serviço
        </h3>
        <p className="text-sm text-gray-600 mt-2">
          Acompanhe o progresso de cada etapa do serviço em tempo real
        </p>
      </div>

      {/* Fases */}
      <div className="space-y-6">
        {fases.map((fase, index) => (
          <div key={fase.id} className="relative">
            {/* Linha conectora */}
            {index < fases.length - 1 && (
              <div className="absolute left-6 top-16 w-0.5 h-12 bg-gradient-to-b from-blue-200 to-transparent"></div>
            )}
            
            {/* Card da Fase */}
            <div className={`relative overflow-hidden rounded-xl border transition-all duration-300 hover:shadow-md ${getStatusColor(fase.status_fase)}`}>
              {/* Barra de Status */}
              <div className={`h-1 w-full ${
                (fase.status_fase || '') === 'Concluída' 
                  ? 'bg-gradient-to-r from-green-400 to-green-600' 
                  : (fase.status_fase || '') === 'Em Rota'
                  ? 'bg-gradient-to-r from-blue-400 to-blue-600'
                  : 'bg-gradient-to-r from-gray-300 to-gray-400'
              }`}></div>
              
              <div className="p-4 lg:p-6">
                {/* Header da Fase */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 space-y-3 lg:space-y-0">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm">
                        {getStatusIcon(fase.status_fase, fase.nome_fase)}
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        {fase.nome_fase || 'Fase não informada'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {getFaseDescription(fase.nome_fase)}
                      </p>
                    </div>
                  </div>
                  
                  <span className={`px-4 py-2 rounded-full text-sm font-medium shadow-sm ${
                    (fase.status_fase || '') === 'Concluída' 
                      ? 'bg-green-100 text-green-800 border border-green-200' 
                      : (fase.status_fase || '') === 'Em Rota'
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-gray-100 text-gray-800 border border-gray-200'
                  }`}>
                    {getStatusText(fase.status_fase || 'Pendente')}
                  </span>
                </div>
                
                {/* Informações da Fase */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                  {/* Endereço */}
                  {fase.endereco_destino && (
                    <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-gray-100">
                      <MapPinIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Destino</p>
                        <p className="text-sm text-gray-900 font-medium">
                          {fase.endereco_destino}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Motorista */}
                  {fase.motorista && fase.motorista.nome && (
                    <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-gray-100">
                      <User className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Motorista</p>
                        <p className="text-sm text-gray-900 font-medium">
                          {fase.motorista.nome}{fase.veiculo ? ` - ${formatarVeiculo(fase.veiculo)}` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Timestamps */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                  {fase.timestamp_inicio_rota && (
                    <div className="text-center p-2 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500 font-medium">Início da Rota</p>
                      <p className="text-sm text-gray-900 font-semibold">
                        {formatTimestamp(fase.timestamp_inicio_rota)}
                      </p>
                    </div>
                  )}
                  
                  {fase.timestamp_chegada && (
                    <div className="text-center p-2 bg-green-50 rounded-lg">
                      <p className="text-xs text-green-600 font-medium">Conclusão</p>
                      <p className="text-sm text-green-900 font-semibold">
                        {formatTimestamp(fase.timestamp_chegada)}
                      </p>
                    </div>
                  )}
                  
                  {fase.ultima_atualizacao && fase.status_fase === 'Em Rota' && (
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-600 font-medium">Última Atualização</p>
                      <p className="text-sm text-blue-900 font-semibold">
                        {formatTimestamp(fase.ultima_atualizacao)}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Rastreamento Ativo */}
                {fase.status_fase === 'Em Rota' && fase.status_rastreamento === 'ativo' && (
                  <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-sm text-blue-800 font-medium">
                        🚀 Rastreamento ativo em tempo real
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Mapa da Fase */}
                {fase.status_fase === 'Em Rota' && (fase.ultima_lat && fase.ultima_lng) && (
                  <div className="w-full">
                    <div className="flex items-center space-x-2 mb-3">
                      <MapPinIcon className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-700">
                        Localização atual do motorista
                      </span>
                    </div>
                    <div className="h-72 lg:h-80 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                      <FaseMap fase={fase} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Resumo */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-2xl font-bold text-gray-900">{fases.length}</p>
            <p className="text-sm text-gray-600">Total de Fases</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">
              {fases.filter(f => f.status_fase === 'Concluída').length}
            </p>
            <p className="text-sm text-green-600">Concluídas</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">
              {fases.filter(f => f.status_fase === 'Em Rota').length}
            </p>
            <p className="text-sm text-blue-600">Em Andamento</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Timeline;
